/**
 * Nike-style filter chips: hide-on-scroll sticky bar + single open popover.
 * Apply-on-change stays on FacetsFormComponent / facet-inputs — this file only does UX chrome.
 *
 * Popovers use position:fixed anchored to the chip — ancestors (.facets-chips__scroll)
 * use overflow-x:auto which forces overflow-y clipping and hides absolute panels.
 */

const MQ_MOBILE = '(max-width: 749px)';
/** Gap under sticky chip bar */
const POPOVER_GAP = 4;
const POPOVER_VIEWPORT_PAD = 8;
/** Above grid + chip bar; below header menu / drawer overlays */
const POPOVER_Z_INDEX = 15;

class FacetsChipsBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {number} */
  #lastScrollY = 0;
  /** @type {HTMLElement | null} */
  #lastFocusedChip = null;
  /** @type {HTMLDetailsElement | null} */
  #trapPanel = null;

  connectedCallback() {
    this.#lastScrollY = window.scrollY;
    this.#bindHideOnScroll();
    this.#bindChipToggle();
    this.#bindChipExclusiveOpen();
    this.#bindOutsideClose();
    this.#bindChipClear();
    this.#measureSpacer();
    window.addEventListener('resize', this.#onResize, { passive: true });
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    window.removeEventListener('resize', this.#onResize);
    document.removeEventListener('click', this.#onDocClick);
    document.removeEventListener('keydown', this.#onKeydown, true);
  }

  #onResize = () => {
    this.#measureSpacer();
    if (this.#trapPanel?.open) this.#positionPopover(this.#trapPanel);
  };

  #measureSpacer() {
    const bar = this.querySelector('[data-facets-chips-bar]');
    const spacer = this.querySelector('[data-facets-chips-spacer]');
    if (!(bar instanceof HTMLElement) || !(spacer instanceof HTMLElement)) return;
    spacer.style.height = `${bar.offsetHeight}px`;
  }

  /**
   * Real user scroll closes open chips (desired). No programmatic scroll from focus
   * because we use preventScroll — so no suppress-close flag needed.
   */
  #bindHideOnScroll() {
    const sentinel = this.querySelector('[data-facets-chips-sentinel]');
    const bar = this.querySelector('[data-facets-chips-bar]');
    if (!(sentinel instanceof HTMLElement) || !(bar instanceof HTMLElement)) return;

    this.#io = new IntersectionObserver(
      ([entry]) => {
        if (!window.matchMedia(MQ_MOBILE).matches) {
          bar.classList.remove('is-hidden-on-scroll');
          return;
        }
        const scrollingDown = window.scrollY > this.#lastScrollY;
        this.#lastScrollY = window.scrollY;
        if (!entry.isIntersecting && scrollingDown && window.scrollY > 80) {
          // Close popovers before transform — transform would trap/clip fixed panels
          this.#closeAllChipPanels();
          bar.classList.add('is-hidden-on-scroll');
        } else if (entry.isIntersecting || !scrollingDown) {
          bar.classList.remove('is-hidden-on-scroll');
        }
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this.#io.observe(sentinel);

    window.addEventListener(
      'scroll',
      () => {
        if (!window.matchMedia(MQ_MOBILE).matches) return;
        const scrollingDown = window.scrollY > this.#lastScrollY + 4;
        const scrollingUp = window.scrollY < this.#lastScrollY - 4;
        if (scrollingUp) {
          bar.classList.remove('is-hidden-on-scroll');
        } else if (scrollingDown && window.scrollY > 80) {
          const rect = sentinel.getBoundingClientRect();
          if (rect.bottom < 0) {
            this.#closeAllChipPanels();
            bar.classList.add('is-hidden-on-scroll');
          }
        }
        this.#lastScrollY = window.scrollY;
      },
      { passive: true }
    );
  }

  /**
   * Anchor popover to chip via position:fixed so overflow on the horizontal scroller
   * cannot clip it. Also overrides floating-panel inline top/left.
   * @param {HTMLDetailsElement} details
   */
  #positionPopover(details) {
    const summary = details.querySelector('summary');
    const panel = details.querySelector('.facets__panel-content');
    const bar = this.querySelector('[data-facets-chips-bar]');
    if (!(summary instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;

    // Anchor sheet under the sticky chip bar (full width) — reliable opaque card
    const barBottom =
      bar instanceof HTMLElement ? bar.getBoundingClientRect().bottom : summary.getBoundingClientRect().bottom;
    const top = barBottom + POPOVER_GAP;
    const left = POPOVER_VIEWPORT_PAD;
    const width = Math.max(200, window.innerWidth - POPOVER_VIEWPORT_PAD * 2);
    const maxHeight = Math.max(160, Math.min(window.innerHeight * 0.6, window.innerHeight - top - POPOVER_VIEWPORT_PAD));

    this.classList.add('is-popover-open');
    panel.classList.add('facets__panel-content--chip-open');

    // CSS variables drive facets-chips.css (wins over floating-panel inline top/left)
    panel.style.setProperty('--chip-pop-top', `${top}px`);
    panel.style.setProperty('--chip-pop-left', `${left}px`);
    panel.style.setProperty('--chip-pop-width', `${width}px`);
    panel.style.setProperty('--chip-pop-max-h', `${maxHeight}px`);
    this.style.setProperty('--chip-pop-top', `${top}px`);

    // Nuclear inline styles — custom elements default to display:inline (no bg box)
    panel.style.cssText = [
      `position:fixed !important`,
      `display:block !important`,
      `top:${top}px !important`,
      `left:${left}px !important`,
      `width:${width}px !important`,
      `max-height:${maxHeight}px !important`,
      `z-index:${POPOVER_Z_INDEX} !important`,
      `background:#fff !important`,
      `background-color:#fff !important`,
      `opacity:1 !important`,
      `visibility:visible !important`,
      `overflow-x:hidden !important`,
      `overflow-y:auto !important`,
      `padding:12px !important`,
      `border-radius:12px !important`,
      `border:1px solid rgba(0,0,0,.12) !important`,
      `box-shadow:0 10px 36px rgba(0,0,0,.18) !important`,
      `box-sizing:border-box !important`,
      `pointer-events:auto !important`,
      `--chip-pop-top:${top}px`,
      `--chip-pop-left:${left}px`,
      `--chip-pop-width:${width}px`,
      `--chip-pop-max-h:${maxHeight}px`,
    ].join(';');

    panel.querySelectorAll('.facets__inputs-list-item.hidden, .facets__inputs-list-item.mobile\\:hidden').forEach((li) => {
      li.classList.remove('hidden', 'mobile:hidden');
    });
    panel.querySelectorAll('.show-more__button').forEach((btn) => {
      if (btn instanceof HTMLElement) btn.style.setProperty('display', 'none', 'important');
    });

    // floating-panel.js may rewrite top/left on mutation — re-assert next frame
    requestAnimationFrame(() => {
      panel.style.setProperty('position', 'fixed', 'important');
      panel.style.setProperty('top', `${top}px`, 'important');
      panel.style.setProperty('left', `${left}px`, 'important');
      panel.style.setProperty('background', '#fff', 'important');
      panel.style.setProperty('background-color', '#fff', 'important');
    });
  }

  /** @param {HTMLDetailsElement} details */
  #clearPopoverPosition(details) {
    const panel = details.querySelector('.facets__panel-content');
    this.classList.remove('is-popover-open');
    this.style.removeProperty('--chip-pop-top');
    if (!(panel instanceof HTMLElement)) return;
    panel.classList.remove('facets__panel-content--chip-open');
    panel.style.cssText = '';
  }

  #bindChipToggle() {
    // Bubble: native <details> already toggled; stopPropagation fences outside-close.
    this.addEventListener('click', (event) => {
      if (!window.matchMedia(MQ_MOBILE).matches) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-chip-clear]')) return;

      const summary = event.target.closest('summary.facets__summary--chip');
      if (!(summary instanceof HTMLElement)) return;

      event.stopPropagation();

      const details = summary.closest('details.facets__panel');
      if (!(details instanceof HTMLDetailsElement)) return;

      if (!details.open) {
        summary.setAttribute('aria-expanded', 'false');
        this.#clearPopoverPosition(details);
        return;
      }

      for (const other of this.querySelectorAll('details.facets__panel[open]')) {
        if (other !== details) {
          other.open = false;
          this.#clearPopoverPosition(other);
        }
      }
      summary.setAttribute('aria-expanded', 'true');
      this.#lastFocusedChip = summary;
      this.#trapPanel = details;
      // Position after open + after floating-panel may write inline styles
      requestAnimationFrame(() => this.#positionPopover(details));
    });
  }

  #bindChipExclusiveOpen() {
    this.addEventListener('toggle', (event) => {
      if (!window.matchMedia(MQ_MOBILE).matches) return;
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement)) return;
      if (!details.classList.contains('facets__panel')) return;

      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      }

      if (!details.open) {
        this.#clearPopoverPosition(details);
        if (this.#trapPanel === details) {
          this.#trapPanel = null;
          this.#lastFocusedChip?.focus({ preventScroll: true });
        }
        return;
      }

      for (const other of this.querySelectorAll('details.facets__panel[open]')) {
        if (other !== details) {
          other.open = false;
          this.#clearPopoverPosition(other);
        }
      }

      this.#lastFocusedChip = summary instanceof HTMLElement ? summary : null;
      this.#trapPanel = details;
      requestAnimationFrame(() => {
        this.#positionPopover(details);
        // preventScroll: no programmatic page scroll → hide-on-scroll won't false-close
        const focusable = details.querySelector(
          'input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusable instanceof HTMLElement) {
          focusable.focus({ preventScroll: true });
        }
      });
    });
  }

  #closeAllChipPanels() {
    for (const d of this.querySelectorAll('details.facets__panel[open]')) {
      this.#clearPopoverPosition(d);
      d.open = false;
    }
    this.#trapPanel = null;
  }

  #onDocClick = (event) => {
    if (!window.matchMedia(MQ_MOBILE).matches) return;
    if (!(event.target instanceof Element)) return;

    if (
      event.target.closest(
        '.facets__item--chip-quick, [data-facets-chips-bar], .facets__panel-content--chip-open, .facets__panel--chip-quick'
      )
    ) {
      return;
    }
    if (event.target.closest('#filters-drawer')) return;

    this.#closeAllChipPanels();
  };

  #onKeydown = (event) => {
    if (!window.matchMedia(MQ_MOBILE).matches) return;

    if (event.key === 'Tab' && this.#trapPanel?.open) {
      const focusables = [
        ...this.#trapPanel.querySelectorAll(
          'input:not([disabled]), button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((el) => el instanceof HTMLElement);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
      return;
    }

    if (event.key !== 'Escape') return;
    const open = this.querySelector('details.facets__panel[open]');
    if (!(open instanceof HTMLDetailsElement)) return;
    this.#clearPopoverPosition(open);
    open.open = false;
    open.querySelector('summary')?.focus({ preventScroll: true });
  };

  #bindOutsideClose() {
    document.addEventListener('click', this.#onDocClick);
    document.addEventListener('keydown', this.#onKeydown, true);
  }

  #bindChipClear() {
    this.addEventListener('click', (event) => {
      const btn = event.target instanceof Element ? event.target.closest('[data-chip-clear]') : null;
      if (!(btn instanceof HTMLElement)) return;
      event.preventDefault();
      event.stopPropagation();

      const item = btn.closest('.facets__item--chip-quick');
      if (!(item instanceof HTMLElement)) return;
      const inputsRoot = item.querySelector('facet-inputs-component');
      inputsRoot?.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
        if (input instanceof HTMLInputElement) input.checked = false;
      });

      const details = item.querySelector('details.facets__panel');
      if (details instanceof HTMLDetailsElement) {
        this.#clearPopoverPosition(details);
        details.open = false;
      }

      const form = this.closest('facets-form-component');
      if (form && typeof form.updateFilters === 'function') {
        form.updateFilters();
      }
    });
  }
}

if (!customElements.get('facets-chips-bar')) {
  customElements.define('facets-chips-bar', FacetsChipsBar);
}

class FacetsSidebarToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#toggle);
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.#toggle();
      }
    });
  }

  #toggle = () => {
    const wrapper = document.querySelector('.collection-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;
    const hidden = wrapper.classList.toggle('facets-sidebar-hidden');
    this.setAttribute('aria-pressed', hidden ? 'true' : 'false');
    const showLabel = this.dataset.labelShow || 'Показати фільтри';
    const hideLabel = this.dataset.labelHide || 'Сховати фільтри';
    const labelEl = this.querySelector('[data-facets-sidebar-toggle-label]');
    if (labelEl) labelEl.textContent = hidden ? showLabel : hideLabel;
  };
}

if (!customElements.get('facets-sidebar-toggle')) {
  customElements.define('facets-sidebar-toggle', FacetsSidebarToggle);
}
